import MiniAppContainer from "./MiniAppContainer";
import styles from "../../styles/Components/mini-app/MiniSettings.module.css";
import MinimalCard from "./MinimalCard";
import { useSelector } from "react-redux";
import useUserData from "../../hooks/useUserData";
import Avatar from "react-avatar";
import { Skeleton } from "antd";
import capitalize from "antd/es/_util/capitalize";
import { BiEditAlt } from "react-icons/bi";
import MiniAppHeader from "./layout/MiniAppHeader";
import {
  FaCircleArrowRight,
  FaRegStar,
  FaCrown,
  FaRegCreditCard,
  FaAward,
  FaGift,
  FaUsers,
  FaWallet,
  FaCircleCheck,
} from "react-icons/fa6";
import MiniExchangesButton from "./MiniExchangesButton";
import { IoStorefrontOutline } from "react-icons/io5";
import { MdLock, MdSettings } from "react-icons/md";
import ChangeAvatar from "../../shared/ChangeAvatar";
import { useState } from "react";
import useSubscriptionPlans from "../../hooks/useSubscriptionPlans";
import useServiceStatus from "../../hooks/useServiceStatus";
import { dateString, expiredTokenHandler, formatPrice } from "../../utility/helper";
import Popup from "../../ui/Popup";
import RedeemGift from "../../pages/pricing-plan/RedeemGift";
import InviteFriendsModal from "./mini-pricing/InviteFriendsModal";

function ServiceItem({ serviceName, isActive }) {
  return (
    <div className={styles.serviceItem}>
      <span>{serviceName}</span>
      {isActive ? (
        <FaCircleCheck size={11} className={styles.activeServiceIcon} />
      ) : (
        <MdLock size={14} className={styles.inactiveServiceIcon} />
      )}
    </div>
  );
}

function MiniSettings() {
  const {
    profile: { profileData, loading },
    alertSystem: { telegramBotToken, telegramBotTokenStatus },
  } = useSelector((state) => state);

  const { email, username } = useUserData();
  const [showInviteModal, setShowInviteModal] = useState(false);

  const { isActive: isInsightActive } = useServiceStatus("insight");
  const { isActive: isSimulatorActive } = useServiceStatus("tradeSimulator");
  const { isActive: isBotActive } = useServiceStatus("bot");
  const { isActive: isAiSignalActive } = useServiceStatus("aiAssistant");
  const { isActive: isAlertActive } = useServiceStatus("alert");
  const totalServiceCount = 5;
  const activeServiceCount =
    Number(isBotActive) +
    Number(isInsightActive) +
    Number(isAiSignalActive) +
    Number(isAlertActive) +
    Number(isSimulatorActive);
  const noActiveService =
    !isBotActive &&
    !isInsightActive &&
    !isAiSignalActive &&
    !isAlertActive &&
    !isSimulatorActive;

  const {
    planTitle,
    isServiceBased,
    iconColor,
    hasSubscription,
    subscriptionData,
    expirationDate,
    isEnterprise,
  } = useSubscriptionPlans();

  const logout = () => {
    expiredTokenHandler();
  };

  const openPlatform = (path) => () => {
    if (telegramBotTokenStatus === "loading") return;
    const url = `https://app.cryptocrispy.com.au/redirect?authToken=${telegramBotToken}&next=${path}`;

    if (window?.Telegram?.WebApp) {
      window.Telegram.WebApp.openLink(url);
    } else {
      window.open(url, "_blank");
    }
  };

  return (
    <MiniAppContainer>
      <MiniAppHeader page="Settings" />

      <MinimalCard customClassName={`${styles.profile} ${styles.box}`}>
        <div className={styles.profileDetail}>
          <div className={styles.avatar}>
            {profileData?.avatar_url ? (
              <img
                className={styles.profileImg}
                src={profileData?.avatar_url}
                alt="avatar"
              />
            ) : (
              <Avatar name={username} size="100%" round={true} />
            )}
          </div>
          <div className={styles.userInfo}>
            <h4>{capitalize(username)}</h4>
            <span>{email}</span>
            <div className={styles.profileCredit}>
              <FaWallet size={12} />
              <span className={styles.profileCreditLabel}>
                Account Balance:
              </span>
              <span className={styles.profileCreditValue}>
                {loading ? (
                  <Skeleton.Input active size="small" />
                ) : (
                  `$${formatPrice(profileData?.credit / 100)}`
                )}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.edit}>
          <ChangeAvatar icon={<BiEditAlt size={20} />} spinSize="small" />
          {/*<BiEditAlt size={20} />*/}
        </div>
      </MinimalCard>

      <MinimalCard customClassName={`${styles.box} ${styles.settingsBox}`}>
        <Popup
          title={
            <div className={styles.settingItem}>
              <div className={styles.label}>
                <span className={styles.icon}>
                  <FaRegCreditCard size={18} />
                </span>
                <span>Subscription</span>
              </div>
              <div className={styles.rightSection}>
                {loading ? (
                  <Skeleton.Input active size="small" />
                ) : (
                  <div className={styles.planBadge}>
                    <FaAward size={14} />
                    <span>
                      {planTitle === "Free Trial" ? "Free" : planTitle}-Plan
                    </span>
                  </div>
                )}
              </div>
            </div>
          }
          height="fit-content"
          closePopup={true}
          popupClassName={styles.popup}
          btnClassName={styles.popupBtn}
        >
          <div className={styles.subscriptionPopupContent}>
            <FaRegCreditCard size={60} color="#0c4cfc" />
            <div className={styles.subscriptionDetails}>
              <div className={styles.planNameRow}>
                <span className={styles.planName}>
                  {planTitle} Plan {!!isServiceBased && "(Service based)"}
                  {hasSubscription &&
                    subscriptionData?.planType &&
                    ` (${subscriptionData?.interval})`}
                </span>
                {subscriptionData?.isCanceled && (
                  <span className={styles.cancelBadge}>Cancelled</span>
                )}
              </div>
              {planTitle !== "Free Trial" && (
                <div className={styles.planDateRow}>
                  <span className={styles.planDateLabel}>
                    {hasSubscription &&
                    !subscriptionData?.isCanceled &&
                    !isEnterprise
                      ? "Next Charge Date:"
                      : "Valid until:"}
                  </span>
                  <span className={styles.planDateValue}>
                    {hasSubscription &&
                    !subscriptionData?.isCanceled &&
                    !isEnterprise
                      ? dateString(subscriptionData?.periodEnd)
                      : expirationDate}
                  </span>
                </div>
              )}
            </div>
            <p className={styles.popupText}>
              To view, buy or manage your subscription, please visit our desktop
              app.
            </p>
            <span
              onClick={openPlatform("subscription")}
              className={`${styles.btn} ${styles.addBtn} ${telegramBotTokenStatus === "loading" ? styles.disabled : ""}`}
            >
              Visit Desktop App
            </span>
          </div>
        </Popup>

        <Popup
          title={
            <div className={styles.settingItem}>
              <div className={styles.label}>
                <span className={styles.icon}>
                  <MdSettings size={16} />
                </span>
                <span>Active Services</span>
              </div>
              <div className={styles.rightSection}>
                <span className={styles.servicesCounter}>
                  {activeServiceCount} of {totalServiceCount}
                </span>
              </div>
            </div>
          }
          height="fit-content"
          closePopup={true}
          popupClassName={styles.popup}
          btnClassName={styles.popupBtn}
        >
          <div className={styles.servicesPopupContent}>
            {/*<MdSettings size={48} color="#0c4cfc" />*/}
            <h4 className={styles.servicesPopupTitle}>
              Active Services{" "}
              <span className={styles.servicesCounter}>
                {activeServiceCount} of {totalServiceCount}
              </span>
            </h4>

            {loading ? (
              <Skeleton.Input active block />
            ) : noActiveService ? (
              <div className={styles.servicesNoActive}>
                <p>No individual services active.</p>
              </div>
            ) : (
              <div className={styles.servicesList}>
                <ServiceItem isActive={isAiSignalActive} serviceName="AI" />
                <ServiceItem isActive={isBotActive} serviceName="Bot" />
                <ServiceItem
                  isActive={isInsightActive}
                  serviceName="Insights"
                />
                <ServiceItem isActive={isAlertActive} serviceName="Alert" />
                <ServiceItem
                  isActive={isSimulatorActive}
                  serviceName="Trade Simulator"
                />
              </div>
            )}

            <p className={styles.servicesPopupText}>
              To manage your services and view additional details, please visit
              our desktop app.
            </p>
            <span
              onClick={openPlatform("services")}
              className={`${styles.btn} ${styles.addBtn} ${telegramBotTokenStatus === "loading" ? styles.disabled : ""}`}
            >
              Visit Desktop App
            </span>
          </div>
        </Popup>

        <a
          href="https://play.google.com/store/apps/details?id=app.cryptocrispy.com.au.com.cryptocrispy.app&pcampaignid=web_share"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.settingItem}
        >
          <div className={styles.label}>
            <span className={styles.icon}>
              <FaRegStar size={14} />
            </span>
            <span>Rate us</span>
          </div>
          <FaCircleArrowRight />
        </a>
        <MiniExchangesButton
          title={
            <div className={styles.settingItem}>
              <div className={styles.label}>
                <span className={styles.icon}>
                  <IoStorefrontOutline size={14} />
                </span>
                <span>Exchanges</span>
              </div>
              <FaCircleArrowRight />
            </div>
          }
        />

        <Popup
          title={
            <div className={styles.settingItem}>
              <div className={styles.label}>
                <span className={styles.icon}>
                  <FaGift size={14} />
                </span>
                <span>Redeem Gift Code</span>
              </div>
              <FaCircleArrowRight />
            </div>
          }
          height="fit-content"
          closePopup={true}
          popupClassName={styles.popup}
          btnClassName={styles.popupBtn}
        >
          <div className={styles.redeemPopupContent}>
            <RedeemGift customClassName={styles.miniRedeemGift} />
          </div>
        </Popup>

        <div
          className={styles.settingItem}
          onClick={() => setShowInviteModal(true)}
        >
          <div className={styles.label}>
            <span className={styles.icon}>
              <FaUsers size={14} />
            </span>
            <span>Referral Code</span>
          </div>
          <FaCircleArrowRight />
        </div>
      </MinimalCard>

      <InviteFriendsModal show={showInviteModal} setShow={setShowInviteModal} />

      <div className={styles.logoutBtn} onClick={logout}>
        Logout
      </div>
    </MiniAppContainer>
  );
}

export default MiniSettings;
